import React, { useState, useEffect, useRef, useCallback } from "react";
import MacroCalculator from "./MacroCalculator";
import { peopleAPI } from "../services/peopleAPI";
import "../styles/MacroCalculator.css";

// Per-device: each device remembers who it was last used for, so a shared
// account on two devices does not fight over one setting. The stored id is
// always validated against the fetched list, so a value left by another
// account simply falls through to that account's default person.
const ACTIVE_PERSON_KEY = "macroCalculator.activePersonId";

const MacroCalculatorPage = () => {
  const [people, setPeople] = useState([]);
  const [activePersonId, setActivePersonId] = useState(null);
  const [peopleLoaded, setPeopleLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [showPeopleModal, setShowPeopleModal] = useState(false);

  // MacroCalculator registers a function here that flushes its pending
  // debounced draft save. Called before a switch so the last few hundred ms of
  // edits are not lost when the component unmounts.
  const flushRef = useRef(null);

  const loadPeople = useCallback(async (preferredId = null) => {
    const { people: list, defaultPersonId } = await peopleAPI.list();
    setPeople(list);

    const stored =
      preferredId ?? parseInt(localStorage.getItem(ACTIVE_PERSON_KEY), 10);
    const valid = list.some((p) => p.id === stored);
    const next = valid ? stored : defaultPersonId ?? list[0]?.id ?? null;

    if (next != null) localStorage.setItem(ACTIVE_PERSON_KEY, String(next));
    setActivePersonId(next);
    return list;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await loadPeople();
      } catch (error) {
        console.error("Error loading people:", error);
        setLoadError("Could not load people for this account.");
      } finally {
        setPeopleLoaded(true);
      }
    })();
  }, [loadPeople]);

  const selectPerson = async (id) => {
    if (id === activePersonId) return;
    // Persist person A's in-flight draft before the remount discards its timer.
    try {
      await flushRef.current?.();
    } catch (error) {
      console.error("Draft flush failed:", error);
    }
    localStorage.setItem(ACTIVE_PERSON_KEY, String(id));
    setActivePersonId(id);
  };

  // Called when a macro request 403s because the active person no longer
  // exists (e.g. deleted from another device). Reset rather than retry-loop.
  const handlePersonGone = useCallback(async () => {
    localStorage.removeItem(ACTIVE_PERSON_KEY);
    try {
      await loadPeople();
    } catch (error) {
      console.error("Error recovering from missing person:", error);
    }
  }, [loadPeople]);

  const registerDraftFlush = useCallback((fn) => {
    flushRef.current = fn;
  }, []);

  // Gate the calculator until the active person is known. Mounting it with a
  // null personId would make its mount effect load the DEFAULT person's draft,
  // which the autosave could then write back under the selected person.
  if (!peopleLoaded) {
    return (
      <div className="macro-calculator-container">
        <h1>Macro Calculator</h1>
        <p className="empty-message">Loading…</p>
      </div>
    );
  }

  if (loadError || activePersonId == null) {
    return (
      <div className="macro-calculator-container">
        <h1>Macro Calculator</h1>
        <p className="empty-message">{loadError || "No person available."}</p>
      </div>
    );
  }

  return (
    <>
      <MacroCalculator
        // Remounting on switch resets all of the calculator's local state at
        // once - exhaustive by construction, unlike a hand-written reset.
        key={activePersonId}
        personId={activePersonId}
        people={people}
        onSelectPerson={selectPerson}
        onManagePeople={() => setShowPeopleModal(true)}
        onPersonGone={handlePersonGone}
        registerDraftFlush={registerDraftFlush}
      />

      {showPeopleModal && (
        <ManagePeopleModal
          people={people}
          activePersonId={activePersonId}
          onClose={() => setShowPeopleModal(false)}
          onChanged={(preferredId) => loadPeople(preferredId)}
        />
      )}
    </>
  );
};

// ==================== MANAGE PEOPLE MODAL ====================

const ManagePeopleModal = ({ people, activePersonId, onClose, onChanged }) => {
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [deletingPerson, setDeletingPerson] = useState(null);
  const [deleteStats, setDeleteStats] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn, preferredId = null) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged(preferredId);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    let createdId = null;
    await run(async () => {
      const { person } = await peopleAPI.create({ name });
      createdId = person.id;
      setNewName("");
    });
    if (createdId) await onChanged(createdId);
  };

  const handleRename = async (id) => {
    const name = editingName.trim();
    if (!name) return;
    await run(async () => {
      await peopleAPI.update(id, { name });
      setEditingId(null);
    });
  };

  const handleMove = async (index, delta) => {
    const next = [...people];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    await run(() => peopleAPI.reorder(next.map((p) => p.id)));
  };

  const openDelete = async (person) => {
    setDeletingPerson(person);
    setConfirmName("");
    setDeleteStats(null);
    setError(null);
    try {
      const { stats } = await peopleAPI.stats(person.id);
      setDeleteStats(stats);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDelete = async () => {
    await run(async () => {
      await peopleAPI.remove(deletingPerson.id, confirmName);
      setDeletingPerson(null);
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Manage People</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="people-modal-body">
          {error && <p className="people-modal-error">{error}</p>}

          {!deletingPerson && (
            <>
              <ul className="people-list">
                {people.map((person, index) => (
                  <li key={person.id} className="people-list-item">
                    {editingId === person.id ? (
                      <>
                        <input
                          className="combination-name-input"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          maxLength={100}
                          autoFocus
                        />
                        <div className="people-list-actions">
                          <button
                            className="confirm-btn"
                            disabled={busy}
                            onClick={() => handleRename(person.id)}
                          >
                            ✓
                          </button>
                          <button className="cancel-btn" onClick={() => setEditingId(null)}>
                            ✕
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="people-list-name">
                          {person.icon ? `${person.icon} ` : ""}
                          {person.name}
                          {person.id === activePersonId && (
                            <span className="people-list-badge">active</span>
                          )}
                        </span>
                        <div className="people-list-actions">
                          <button
                            className="reorder-button"
                            disabled={busy || index === 0}
                            onClick={() => handleMove(index, -1)}
                            aria-label={`Move ${person.name} up`}
                          >
                            ▲
                          </button>
                          <button
                            className="reorder-button"
                            disabled={busy || index === people.length - 1}
                            onClick={() => handleMove(index, 1)}
                            aria-label={`Move ${person.name} down`}
                          >
                            ▼
                          </button>
                          <button
                            className="edit-item-btn"
                            onClick={() => {
                              setEditingId(person.id);
                              setEditingName(person.name);
                            }}
                          >
                            ✎
                          </button>
                          <button
                            className="remove-button-compact"
                            disabled={people.length <= 1}
                            title={
                              people.length <= 1
                                ? "You must keep at least one person"
                                : `Delete ${person.name}`
                            }
                            onClick={() => openDelete(person)}
                          >
                            ×
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>

              <form className="people-add-form" onSubmit={handleAdd}>
                <input
                  className="combination-name-input"
                  placeholder="Add a person…"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  maxLength={100}
                />
                <button className="add-section-btn" type="submit" disabled={busy || !newName.trim()}>
                  Add
                </button>
              </form>
            </>
          )}

          {deletingPerson && (
            <div className="people-delete-confirm">
              <h4>Delete {deletingPerson.name}?</h4>
              <p>
                This permanently deletes everything tracked for this person, in the
                macro calculator and the workout tracker alike. Shared templates,
                meals, folders, custom foods, exercises and workout programs are not
                affected.
              </p>
              {deleteStats && (
                <ul className="people-delete-stats">
                  <li>{deleteStats.daily_logs} daily logs</li>
                  <li>{deleteStats.weight_logs} weight entries</li>
                  <li>{deleteStats.weight_goals} weight goals</li>
                  <li>{deleteStats.goal_presets} goal presets</li>
                  <li>{deleteStats.habits} habits (and their history)</li>
                  <li>{deleteStats.workout_logs} workout logs (and their sets)</li>
                  <li>{deleteStats.active_programs} programs in progress</li>
                  <li>{deleteStats.personal_records} personal records</li>
                </ul>
              )}
              <label className="people-delete-label">
                Type <strong>{deletingPerson.name}</strong> to confirm:
                <input
                  className="combination-name-input"
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  autoFocus
                />
              </label>
              <div className="people-delete-actions">
                <button className="cancel-btn" onClick={() => setDeletingPerson(null)}>
                  Cancel
                </button>
                <button
                  className="remove-section-btn"
                  disabled={busy || confirmName.trim() !== deletingPerson.name}
                  onClick={handleDelete}
                >
                  Delete permanently
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MacroCalculatorPage;
